import { ListBookDto } from '@hermes/domains'
import { useCallback, useMemo } from 'react'
import useSWR from 'swr'
import useSWRInfinite from 'swr/infinite'
import { buildCacheKey } from '../../shared/utils/cache'
import {
  createWishlistItemByBookId as createWishlistItemApi,
  deleteWishlistItemByBookId,
  getWishlist,
  getWishlistBookIds
} from './api'
import { createHash } from '../../shared/utils/createHash'
import { useHermesSdkConfig } from '../../use-hermes-sdk-config'

export const useYourWishlist = (params: { size?: number } = {}) => {
  const { size = 20 } = params
  const config = useHermesSdkConfig()
  const getCacheKey = (pageIndex: number, previousPageData: any) => {
    if (pageIndex > 0 && previousPageData && !previousPageData.data) return null

    return {
      key: 'useYourWishlist',
      context: buildCacheKey('useYourWishlist', { userUuid: createHash(config.accessToken) }, { size }),
      pageIndex
    }
  }

  const {
    data,
    isLoading,
    setSize,
    size: currentPage,
    mutate: dispatchWishlistUpdate,
    error
  } = useSWRInfinite(getCacheKey, ({ pageIndex }) => getWishlist({ page: pageIndex + 1, size }, config), {
    revalidateOnFocus: false,
    errorRetryCount: 0,
    keepPreviousData: true
  })

  const {
    data: bookIds,
    isLoading: isBookIdsLoading,
    mutate: dispatchWishlistIdsUpdate
  } = useSWR(buildCacheKey('useYourWishlist', { schema: 'bookId' }, { size }), () => getWishlistBookIds(config))

  const createWishlistItem = useCallback(
    async (body: { bookId: number }) => {
      if (!body.bookId) return

      dispatchWishlistIdsUpdate()
      await dispatchWishlistUpdate(
        async () => {
          await createWishlistItemApi(body, config)
          return data
        },
        {
          rollbackOnError: true
        }
      )
    },
    [dispatchWishlistUpdate, data]
  )

  const removeWishlistItem = useCallback(
    async (bookId: number) => {
      if (!bookId) return

      const optimisticData = data?.map((page) => ({
        ...page,
        data: page.data.filter((item: ListBookDto) => item.book.id !== bookId)
      }))

      dispatchWishlistIdsUpdate()
      await dispatchWishlistUpdate(
        async () => {
          await deleteWishlistItemByBookId({ bookId }, config)
          return optimisticData
        },
        {
          optimisticData,
          rollbackOnError: true
        }
      )
    },
    [dispatchWishlistUpdate, data]
  )

  const results = useMemo(() => data?.map((page) => page.data).flat(), [data])

  const pagination = useMemo(() => data?.findLast((pg) => pg.pagination)?.pagination, [data])

  const hasMore = pagination?.continue

  const hasWishlistItem = useCallback(
    (bookId: number) => {
      if (isBookIdsLoading || !bookIds) return false
      const item = bookIds.find((id) => id === bookId)
      return item || false
    },
    [isBookIdsLoading, bookIds]
  )

  return {
    data: results,
    isLoading: isLoading || (currentPage > 0 && data && typeof data[currentPage - 1] === 'undefined') || false,
    setSize,
    nextPage: () => hasMore && setSize(currentPage + 1),
    page: currentPage,
    createWishlistItem,
    removeWishlistItem,
    refresh: () => dispatchWishlistUpdate(undefined, { revalidate: true }),
    hasWishlistItem,
    error
  }
}
